/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { useDispatch } from 'react-redux';
import { submit } from 'redux-form';
import { css, StyleSheet } from 'aphrodite';
import { mixins } from '@blend/consumer-components';

import { ChooserChoice } from '../../TaskInputs/types';
import { Choice } from './Choice';

const styles = StyleSheet.create({
  chooser: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    alignSelf: 'center',
    ...mixins.margin(24, 0),
  },
});

export namespace Chooser {
  export interface Props {
    choosers: ChooserChoice[];
    onChoiceSelect: (choiceValue: string) => void;
    formName: string;
  }
}

/**
 * This is ported from the Borrower "TaskChooser" component, but stripped down to only include
 * the Chooser container (this file) and Chooser "choice"" components.
 *
 * This is intended to be used inside of a Redux form. When one of the choices is clicked,
 * the value of the form is set to that choice and the form is submitted.
 */
export const Chooser: React.FunctionComponent<Chooser.Props> = ({ choosers, onChoiceSelect, formName }) => {
  const dispatch = useDispatch();

  const buttonStyle = !!choosers.find(choice => choice.isComplete)
    ? 'secondary' // If every button is not complete
    : 'primary'; // If at least one button is complete

  return (
    <div className={css(styles.chooser)} data-it="chooser">
      {choosers.map((choice, idx) => {
        /**
         * We're using Redux's remote form submit to submit the form as soon
         * as a choice is selected (Input components don't have direct access to the form submit handler.)
         */
        return (
          <Choice
            key={`choice-${idx}`}
            choice={choice}
            buttonStyle={buttonStyle}
            handleChoiceClick={async () => {
              // eslint-disable-next-line @typescript-eslint/await-thenable
              await onChoiceSelect(choice.value);
              dispatch(submit(formName));
            }}
          />
        );
      })}
    </div>
  );
};
